/**
 * Componente AgGridTable para la lista de clientes
 * 
 * Este componente renderiza la tabla de clientes utilizando AG Grid en lugar de VirtualTable.
 * Incluye funcionalidades de:
 * - Infinite scroll
 * - Columnas personalizables
 */

import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef } from 'react';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { Client } from '../../models/Clients';

interface ClientsAgGridTableProps {
  clients: Client[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  refetch?: () => void;
  // 🎯 Props para ColumnSelector
  selectedColumns?: string[];
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para acciones
  onImpersonate?: (userId: number) => void;
}

export const ClientsAgGridTable = React.memo(function ClientsAgGridTable({
  clients,
  isFetching = false,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onImpersonate,
}: ClientsAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => { }, []);

  // 🎯 Usar el hook para manejar las columnas (ahora con cellRenderers incluidos)
  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    onImpersonate,
  });

  // 🎯 Las columnas ya vienen con sus cellRenderers desde el hook
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  return (
    <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
      <AgGridTable
        ref={agGridRef}
        data={clients}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 150px)"
        enableSelection={false}
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}}
      />
    </div>
  );
});

